import { useAtomValue } from 'jotai'
import React from 'react'
import * as R from 'remeda'

import { brandAccountIdLastSurveyIdMapAtom } from '~/atoms'
import { exists, findByPath, maxBy, SurveyOp } from '~/utils'

import { useCommonQueryCurrentBrandAccount } from './useCommonQuery'
import { useCurrentBrandAccountSurveysQueryData } from './useCurrentBrandAccountSurveysQuery'
import { useCurrentSurvey } from './useCurrentSurvey'

export const useCurrentPrimarySurvey = () => {
  const brandAccount = useCommonQueryCurrentBrandAccount()
  const surveys = useCurrentBrandAccountSurveysQueryData()
  const brandAccountIdLastSurveyIdMap = useAtomValue(
    brandAccountIdLastSurveyIdMapAtom,
  )

  const getLastSurvey = React.useCallback(() => {
    if (!exists(brandAccount)) {
      return undefined
    }

    const lastSurveyId = brandAccountIdLastSurveyIdMap?.[brandAccount.id]
    if (!exists(lastSurveyId)) {
      return undefined
    }

    return findByPath(surveys, 'id', lastSurveyId)
  }, [brandAccount, brandAccountIdLastSurveyIdMap, surveys])

  const getLatestSurvey = React.useCallback(() => {
    if (!exists(brandAccount) || surveys.length === 0) {
      return undefined
    }

    return R.pipe(
      surveys,
      maxBy(
        (survey) => SurveyOp.getSurveyStartDate(survey)?.getTime() ?? -Infinity,
      ),
    )
  }, [brandAccount, surveys])

  const currentSurvey = useCurrentSurvey()

  return React.useMemo(
    () => currentSurvey.value ?? getLastSurvey() ?? getLatestSurvey(),
    [currentSurvey, getLastSurvey, getLatestSurvey],
  )
}
